import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { environmentNetworkInfoKeys } from "components/Environment/networkInfo";
import { launchableKeys } from "server/brevdeploy/launchables/queries";

interface UseEnvironmentListRefreshOptions {
  loadTeamMembers: () => Promise<void>;
  refetchComputeResources: () => Promise<unknown>;
}

export function useEnvironmentListRefresh({
  loadTeamMembers,
  refetchComputeResources,
}: UseEnvironmentListRefreshOptions) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  const refresh = useCallback(async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      // Refresh ports independently so slow network-info requests do not pin
      // the refresh button. The list update also refreshes active port queries.
      void queryClient
        .invalidateQueries({
          queryKey: environmentNetworkInfoKeys.all,
          refetchType: "active",
        })
        .catch(() => undefined);

      await Promise.allSettled([
        refetchComputeResources(),
        loadTeamMembers(),
        queryClient.invalidateQueries({
          queryKey: launchableKeys.details(),
          refetchType: "active",
        }),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, loadTeamMembers, queryClient, refetchComputeResources]);

  return { isRefreshing, refresh };
}
